import React from "react";
import {
  Card,
  Text,
  Stack,
  Divider,
  Group,
  Flex,
  Badge,
  Paper,
} from "@mantine/core";
import dayjs from "dayjs";
import { LearningPlan } from "../../../types/api";

interface CalendarViewModeProps {
  data: LearningPlan;
  t: (key: string) => string;
}

const CalendarViewMode = ({ data, t }: CalendarViewModeProps) => {
  return (
    <Card withBorder shadow="sm" padding="md" radius="md">
      <Stack gap="sm">
        <Text fw={600} size="lg">
          {data.title}
        </Text>
        <Text c="dimmed" size="sm">
          {data.description || t("no_description")}
        </Text>

        <Group gap="xs">
          <Badge variant="light" color="violet">
            {t(`status.${data.status}`)}
          </Badge>
          {data.deadline && (
            <Badge variant="light" color="blue">
              {t("deadline")}: {dayjs(data.deadline).format("YYYY-MM-DD")}
            </Badge>
          )}
        </Group>

        <Divider
          label={t("calculated_automatically")}
          labelPosition="center"
          mt={10}
        />
        <Stack gap={4} my={10}>
          <Flex justify="space-around" wrap="wrap" align="flex-end">
            <Flex direction="column" align="center" w="25%">
              <Text size="xs" c="dimmed" ta="center">
                {t("estimated_total_hours")}
              </Text>
              <Text ta="center">{data.estimated_total_hours ?? "-"}</Text>
            </Flex>

            <Flex direction="column" align="center" w="25%">
              <Text size="xs" c="dimmed" ta="center">
                {t("progress")}
              </Text>
              <Text ta="center">{data.progress ?? 0}%</Text>
            </Flex>

            <Flex direction="column" align="center" w="25%">
              <Text size="xs" c="dimmed" ta="center">
                {t("total_goals")}
              </Text>
              <Text ta="center">{data.total_goals ?? 0}</Text>
            </Flex>

            <Flex direction="column" align="center" w="25%">
              <Text size="xs" c="dimmed" ta="center">
                {t("completed_goals")}
              </Text>
              <Text ta="center">{data.completed_goals ?? 0}</Text>
            </Flex>
          </Flex>
        </Stack>

        <Divider />

        <Flex direction="column" gap={4}>
          <Text size="xs" c="dimmed">
            {t("skills_to_gain")}
          </Text>

          <Flex wrap="wrap" gap={8}>
            {Array.isArray(data.skills_to_gain) &&
            data.skills_to_gain.length ? (
              data.skills_to_gain.map((i, idx) => (
                <Paper
                  key={idx}
                  px={16}
                  py={6}
                  withBorder
                  radius="xl"
                  maw="100%"
                >
                  <Text
                    size="sm"
                    c="gray.8"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "100%",
                    }}
                  >
                    {i}
                  </Text>
                </Paper>
              ))
            ) : typeof data.skills_to_gain === "string" &&
              data.skills_to_gain.trim() !== "" ? (
              data.skills_to_gain
                .split(",")
                .map((i) => i.trim())
                .map((i, idx) => (
                  <Paper
                    key={idx}
                    px={16}
                    py={6}
                    withBorder
                    radius="xl"
                    maw="100%"
                  >
                    <Text
                      size="sm"
                      c="gray.8"
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "100%",
                      }}
                    >
                      {i}
                    </Text>
                  </Paper>
                ))
            ) : (
              <Text>-</Text>
            )}
          </Flex>
        </Flex>

        <Flex direction="column">
          <Text size="xs" c="dimmed">
            {t("motivation_note")}
          </Text>
          <Text>{data.motivation_note || "-"}</Text>
        </Flex>

        <Flex direction="column">
          <Text size="xs" c="dimmed">
            {t("coaching_notes")}
          </Text>
          <Text>{data.coaching_notes || "-"}</Text>
        </Flex>
      </Stack>
    </Card>
  );
};

export default CalendarViewMode;
